import urlJoin from 'url-join';
import MD5 from 'crypto-js/md5';

export const getURL = () => {
  let url =
    process.env.NEXT_PUBLIC_SITE_URL ?? // Set this to your site URL in production env.
    process.env.NEXT_PUBLIC_VERCEL_URL ?? // Automatically set by Vercel.
    'http://localhost:3002/';
  // Make sure to include `https://` when not localhost.
  url = url.startsWith('http') ? url : `https://${url}`;
  // Make sure to including trailing `/`.
  url = url.charAt(url.length - 1) === '/' ? url : `${url}/`;
  return url;
};

export const toSiteURL = (path: string) => {
  const url = getURL();
  return urlJoin(url, path);
};

export const toDateTime = (secs: number) => {
  const t = new Date('1970-01-01T00:30:00Z'); // Unix epoch start.
  t.setSeconds(secs);
  return t;
};

export const getUserAvatarUrl = ({
  email,
  profileAvatarUrl,
}: {
  email: string;
  profileAvatarUrl?: string;
}) => {
  const placeholderAvatarUrl = `https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp`;
  const fallbackAvatarUrl = `https://www.gravatar.com/avatar/${MD5(
    email
  )}?d=mp`;
  const isProfileAvatarUrlValid =
    profileAvatarUrl && profileAvatarUrl.length > 0;
  return isProfileAvatarUrlValid
    ? profileAvatarUrl
    : fallbackAvatarUrl ?? placeholderAvatarUrl;
};

// Gateway types for multi-entity payment routing
// - 'upgrow' and 'keesy': Primary entity (uses primary env vars)
// - 'up-grow' and 'okgrow': Secondary entity (uses _B env vars)
// - 'creditcaptain': Entity A recovery MID (uses Entity A relay + CC security key)
// - 'newmid': Go-live MID for all billing systems (uses NMI_SECURITY_KEY_NEW; cross-MID only, NOT in isPrimaryGateway)
// - 'mentom': Third MID (Mentom Payments, opened 2026-07; uses NMI_SECURITY_KEY_MNTM; cross-MID only, NOT in isPrimaryGateway)
// - 'kurv':   Fourth MID (boarded 2026-09-27, processor ems / BMO Harris; uses NMI_SECURITY_KEY_KURV; cross-MID only, NOT in isPrimaryGateway)
export type GatewayType = 'upgrow' | 'up-grow' | 'okgrow' | 'keesy' | 'creditcaptain' | 'newmid' | 'mentom' | 'kurv';

// Check if gateway uses primary env vars (Entity A / primary account)
// Note: 'newmid' and 'mentom' are cross-MID-only (charged via the card's source-entity relay) and are
// intentionally NOT primary — they fall through to false (Entity B Chargebee/Evervault for recording).
export const isPrimaryGateway = (gateway: string): boolean => {
  return gateway === 'upgrow' || gateway === 'keesy' || gateway === 'creditcaptain';
};

// MIDs whose stored NTIDs predate them → first charge must establish CoF (stored/customer, OMIT initial_transaction_id). newmid is excluded (dead).
// kurv (2026-09-27) is listed so a kurv approval writes ONLY a per-(card, MID) lineage row — a kurv NTID
// must never be rolled onto `encrypted_cards.initial_transaction_id` (MID-scoped; poisons every other MID).
export const FRESH_COF_MIDS = ['luqra', 'mentom', 'kurv'] as const;

// Security-key-only MIDs (cross-MID relay; no Chargebee integration, no username/password).
// Onboarding a new MID here = one row + the env var (+ a GatewayType member above +
// a FRESH_COF_MIDS entry). A missing/empty env is EXPECTED pre-provisioning — the MID
// registry gates `enabled` on key presence, so an absent key leaves the MID inert,
// never a crash. Consumed by nmiSettingsForGateway below and by vault-mid-pin's
// credential-slot derivation (slot account label = the gateway name for new rows).
export const SECURITY_KEY_ONLY_GATEWAYS: Record<string, string> = {
  creditcaptain: 'NMI_SECURITY_KEY_CC',
  newmid: 'NMI_SECURITY_KEY_NEW',
  mentom: 'NMI_SECURITY_KEY_MNTM',
  kurv: 'NMI_SECURITY_KEY_KURV',
};

export const chargebeeAdminSettingsForSite = (site: string): { site: string, api_key: string } => {
  return isPrimaryGateway(site)
    ? { site: process.env.NEXT_PUBLIC_CHARGEBEE_SITE, api_key: process.env.CHARGEBEE_API_KEY }
    : { site: process.env.NEXT_PUBLIC_CHARGEBEE_B_SITE, api_key: process.env.CHARGEBEE_B_API_KEY};
}
export const chargebeeFrontendSettingsForSite = (site: string): { site: string, publishableKey: string } => {
  return isPrimaryGateway(site)
    ? { site: process.env.NEXT_PUBLIC_CHARGEBEE_SITE, publishableKey: process.env.NEXT_PUBLIC_CHARGEBEE_API_KEY }
    : { site: process.env.NEXT_PUBLIC_CHARGEBEE_B_SITE, publishableKey: process.env.NEXT_PUBLIC_CHARGEBEE_B_API_KEY };
}

// Evervault settings per gateway
export const evervaultSettingsForGateway = (gateway: GatewayType) => {
  const usePrimary = isPrimaryGateway(gateway);

  return {
    teamId: usePrimary
      ? process.env.NEXT_PUBLIC_EVERVAULT_TEAM_ID
      : process.env.NEXT_PUBLIC_EVERVAULT_TEAM_ID_B,
    appId: usePrimary
      ? process.env.NEXT_PUBLIC_EVERVAULT_APP_ID
      : process.env.NEXT_PUBLIC_EVERVAULT_APP_ID_B,
    apiKey: usePrimary
      ? process.env.EVERVAULT_API_KEY
      : process.env.EVERVAULT_API_KEY_B,
    relayUrl: usePrimary
      ? process.env.EVERVAULT_NMI_RELAY_URL
      : process.env.EVERVAULT_NMI_RELAY_URL_B,
    // Apple Pay / Google Pay Merchant ID - each entity has its own merchant
    // Merchant IDs are tied to domain verification and network token provisioning
    merchantId: usePrimary
      ? process.env.NEXT_PUBLIC_EVERVAULT_MERCHANT_ID
      : process.env.NEXT_PUBLIC_EVERVAULT_MERCHANT_ID_B,
  };
};

// NMI settings per gateway
export const nmiSettingsForGateway = (gateway: GatewayType) => {
  // Security-key-only MIDs (creditcaptain / newmid / mentom / future MIDs): the map row
  // IS the branch. Key may be empty until the MID is provisioned; that is expected.
  const securityKeyOnlyEnv = Object.prototype.hasOwnProperty.call(SECURITY_KEY_ONLY_GATEWAYS, gateway)
    ? SECURITY_KEY_ONLY_GATEWAYS[gateway]
    : undefined;
  if (securityKeyOnlyEnv) {
    return {
      securityKey: process.env[securityKeyOnlyEnv],
      username: '',
      password: '',
      chargebeeGatewayId: '',
    };
  }

  const usePrimary = isPrimaryGateway(gateway);

  return {
    securityKey: usePrimary
      ? process.env.NMI_SECURITY_KEY
      : process.env.NMI_SECURITY_KEY_B,
    // Username/password auth - required for Chargebee compatibility
    // Chargebee's NMI gateway uses username/password auth, so vault tokens
    // must be created with the same auth method to be visible to Chargebee
    username: usePrimary
      ? process.env.NMI_USERNAME
      : process.env.NMI_USERNAME_B,
    password: usePrimary
      ? process.env.NMI_PASSWORD
      : process.env.NMI_PASSWORD_B,
    chargebeeGatewayId: usePrimary
      ? process.env.CHARGEBEE_NMI_GATEWAY_ID
      : process.env.CHARGEBEE_NMI_GATEWAY_ID_B,
  };
};

/**
 * Get settings for cross-MID charging.
 * This enables charging cards from one Evervault entity on a different NMI MID.
 *
 * Use case: Entity A encrypted cards can be charged on NMI MID B by using
 * Entity A's Evervault relay (for decryption) with Entity B's NMI credentials (for charging).
 *
 * @param sourceEntity - Which Evervault entity encrypted the card
 * @param targetNmiMid - Which NMI MID to charge on
 */
export const getCrossMidChargeSettings = (
  sourceEntity: 'entity_a' | 'entity_b',
  targetNmiMid: GatewayType
) => {
  // Use SOURCE entity's Evervault relay (for decryption)
  const evervaultSettings =
    sourceEntity === 'entity_a'
      ? {
          relayUrl: process.env.EVERVAULT_NMI_RELAY_URL!,
          apiKey: process.env.EVERVAULT_API_KEY!,
          appId: process.env.NEXT_PUBLIC_EVERVAULT_APP_ID!,
        }
      : {
          relayUrl: process.env.EVERVAULT_NMI_RELAY_URL_B!,
          apiKey: process.env.EVERVAULT_API_KEY_B!,
          appId: process.env.NEXT_PUBLIC_EVERVAULT_APP_ID_B!,
        };

  // Use TARGET NMI MID credentials (for charging)
  const nmiSettings = nmiSettingsForGateway(targetNmiMid);

  // Use TARGET Chargebee for payment recording
  const chargebeeSettings = chargebeeAdminSettingsForSite(targetNmiMid);

  return { evervaultSettings, nmiSettings, chargebeeSettings };
};

export const encodeKey = "ZXCVB", xorEncodeDecode = (input:string) => [...input].map((c, i) => String.fromCharCode(c.charCodeAt(0) ^ encodeKey.charCodeAt(i % encodeKey.length))).join('');
export const chargebeeToUse = async (): Promise<string> => {
  const response = await fetch('/api/egu');
  const data = await response.json();
  const gateway = xorEncodeDecode(data.e); // 'e' is the secret property key for "gateway"
  // upgrow is /($$--
  // up-grow is /(n105/
  return gateway;
}